import React, { useState, useEffect } from "react";
import {
  FiMapPin,
  FiPackage,
  FiTruck,
  FiEye,
  FiRefreshCw,
  FiClock,
  FiNavigation,
  FiMap,
  FiSearch,
  FiX,
} from "react-icons/fi";
import AdminService from "src/services/admin.services";
// import { ConfirmationModal } from "src/components/ui";
import { AppLayout } from "src/components/layouts";
import CustomSelect from "src/components/ui/CustomSelect";
import FoodLoader from "src/components/Loader";
import { useQuery } from "react-query";
import OrderMap from "./components/OrderMap";

const adminService = new AdminService();

interface OrderLocation {
  latitude: number;
  longitude: number;
  lastUpdated: string;
  accuracy?: number;
  speed?: number;
  heading?: number;
}

interface TrackingOrder {
  id: string;
  orderId: string;
  parcelId?: string;
  status:
    | "pending"
    | "assigned"
    | "picked_up"
    | "in_transit"
    | "delivered"
    | "cancelled";
  customerName: string;
  customerPhone: string;
  customerAddress: string;
  pickupAddress: string;
  deliveryAddress: string;
  assignedAgent?: {
    id: string;
    name: string;
    phone: string;
    vehicleType: string;
  };
  currentLocation?: OrderLocation;
  estimatedDelivery?: string;
  createdAt: string;
  updatedAt: string;
  priority: "low" | "medium" | "high";
  orderType: "food" | "parcel" | "grocery";
}

const ActiveRiders: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [orderTypeFilter, setOrderTypeFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState<TrackingOrder | null>(
    null
  );
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [mapView, setMapView] = useState(true);

  // Real orders will be fetched from the API

  // Use React Query for data fetching with automatic revalidation
  const {
    data: orders = [],
    isLoading,
    refetch: loadOrders,
  } = useQuery(
    ["trackingOrders"],
    async () => {
      try {
        // Fetch BOTH food orders and parcels
        const [foodOrders, parcels] = await Promise.all([
          adminService.getOrders(1, 100), // Food orders from shopping/order/search
          adminService.getLogisticsManagement(
            1,
            8.6753,
            9.082,
            100000000000000n,
            "",
            ""
          ), // Parcels from logistics/search
        ]);

        // Transform food orders
        const transformedFoodOrders = Array.isArray(foodOrders)
          ? foodOrders.map((order: any) => ({
              id: order._id,
              orderId: order.order_code,
              parcelId: undefined, // Food orders don't have parcel IDs
              status: (() => {
                // Map API statuses to our tracking statuses
                const apiStatus = order.order_status;
                if (!apiStatus) return "pending";

                const lowerStatus = apiStatus.toLowerCase();

                // Map your actual statuses
                if (lowerStatus === "delivered") {
                  return "delivered" as const;
                } else if (lowerStatus === "cancelled") {
                  return "cancelled" as const;
                } else if (lowerStatus === "declined") {
                  return "cancelled" as const;
                } else if (lowerStatus === "ready") {
                  return "assigned" as const;
                } else if (lowerStatus === "arrived") {
                  return "picked_up" as const;
                } else if (lowerStatus === "enroute") {
                  return "in_transit" as const;
                } else {
                  return "pending" as const;
                }
              })(),
              customerName: "Customer", // Food orders don't have customer name in this response
              customerPhone: "N/A", // Food orders don't have customer phone in this response
              customerAddress: "N/A", // Food orders don't have customer address in this response
              pickupAddress: order.restaurant_name || "Restaurant",
              deliveryAddress: "Delivery Location", // This is an ID reference in your data
              assignedAgent: order.agent
                ? {
                    id: order.agent,
                    name: "Agent", // We only have the ID, not the name
                    phone: "N/A",
                    vehicleType: "Motorcycle",
                  }
                : undefined,
              currentLocation: undefined, // Food orders don't have current location in this response
              estimatedDelivery: undefined, // Food orders don't have estimated delivery in this response
              createdAt: order.created_at,
              updatedAt: order.updated_at || order.created_at,
              priority: "medium" as const,
              orderType: "food" as const, // Food orders are always food type
            }))
          : [];

        // Transform parcels
        const transformedParcels = Array.isArray(parcels)
          ? parcels.map((parcel: any) => ({
              id: parcel._id,
              orderId: parcel.trackingId,
              parcelId: parcel._id, // Use the parcel ID as parcelId
              status: (() => {
                // Map API statuses to our tracking statuses
                const apiStatus = parcel.order_status;
                if (!apiStatus) return "pending";

                const lowerStatus = apiStatus.toLowerCase();

                // Map your actual parcel statuses
                if (lowerStatus === "delivered") {
                  return "delivered" as const;
                } else if (lowerStatus === "picked_up") {
                  return "picked_up" as const;
                } else if (lowerStatus === "enroute") {
                  return "in_transit" as const;
                } else if (lowerStatus === "arrived") {
                  return "picked_up" as const;
                } else {
                  return "pending" as const;
                }
              })(),
              customerName: parcel.receiver?.name || "Unknown Customer",
              customerPhone: parcel.receiver?.phone || "N/A",
              customerAddress: parcel.receiver?.address?.address || "N/A",
              pickupAddress: parcel.pickup_location?.address || "N/A",
              deliveryAddress: parcel.receiver?.address?.address || "N/A",
              assignedAgent: parcel.agentId
                ? {
                    id: parcel.agentId,
                    name: "Agent", // We only have the ID, not the name
                    phone: "N/A",
                    vehicleType: "Motorcycle",
                  }
                : undefined,
              currentLocation: undefined, // Parcels don't have real-time location in this response
              estimatedDelivery: undefined, // Parcels don't have estimated delivery in this response
              createdAt: parcel.created_at,
              updatedAt: parcel.updated_at || parcel.created_at,
              priority: "medium" as const,
              orderType: "parcel" as const, // Parcels are always parcel type
            }))
          : [];

        // Combine both food orders and parcels
        const allDeliveries = [...transformedFoodOrders, ...transformedParcels];

        // Filter out completed/cancelled orders - only show active ones
        const activeDeliveries = allDeliveries.filter(
          (delivery) =>
            !["delivered", "cancelled", "declined"].includes(delivery.status)
        );

        if (activeDeliveries.length > 0) {
          return activeDeliveries;
        } else {
          return [];
        }
      } catch (error) {
        return [];
      }
    },
    {
      refetchInterval: 30 * 1000, // Refetch every 30 seconds for real-time tracking
      refetchOnWindowFocus: true,
      staleTime: 10 * 1000, // Data is fresh for 10 seconds
    }
  );

  // Note: Real-time tracking is now handled by the OrderMap component
  // which calls the tracking APIs directly for each order/parcel

  const handleViewOrder = (order: TrackingOrder) => {
    setSelectedOrder(order);
    setShowOrderDetails(true);
  };

  const handleRefreshTracking = () => {
    loadOrders();
    // If we're in table view and have a selected order, refresh the modal map too
    if (!mapView && selectedOrder && showOrderDetails) {
      // Force a re-render of the modal to refresh the map
      setShowOrderDetails(false);
      setTimeout(() => {
        setShowOrderDetails(true);
        setSelectedOrder(selectedOrder);
      }, 100);
    }
  };

  // Initialize modal map when modal opens
  useEffect(() => {
    if (showOrderDetails && selectedOrder) {
      const initializeModalMap = () => {
        const mapElement = document.getElementById(
          `modal-map-${selectedOrder.id}`
        );

        if (mapElement && window.google) {
          try {
            const center = selectedOrder.currentLocation
              ? {
                  lat: selectedOrder.currentLocation.latitude,
                  lng: selectedOrder.currentLocation.longitude,
                }
              : { lat: 9.082, lng: 8.6753 }; // Default to Abuja

            const modalMap: google.maps.Map = new window.google.maps.Map(
              mapElement,
              {
                center: center,
                zoom: 12,
                mapTypeId: window.google.maps.MapTypeId.ROADMAP,
                styles: [
                  {
                    featureType: "administrative.locality",
                    elementType: "labels.text",
                    stylers: [{ visibility: "on" }],
                  },
                  {
                    featureType: "administrative.neighborhood",
                    elementType: "labels.text",
                    stylers: [{ visibility: "on" }],
                  },
                  {
                    featureType: "poi",
                    elementType: "labels.text",
                    stylers: [{ visibility: "on" }],
                  },
                  {
                    featureType: "road",
                    elementType: "labels.text",
                    stylers: [{ visibility: "on" }],
                  },
                ],
                disableDefaultUI: false,
                zoomControl: true,
                mapTypeControl: true,
                scaleControl: true,
                streetViewControl: true,
                fullscreenControl: true,
                gestureHandling: "cooperative",
                backgroundColor: "#f8f9fa",
                clickableIcons: true,
                draggable: true,
                scrollwheel: true,
              }
            );

            // Add marker for the selected order
            let orderMarker: google.maps.Marker | null = null;

            if (selectedOrder.currentLocation) {
              orderMarker = new window.google.maps.Marker({
                position: {
                  lat: selectedOrder.currentLocation.latitude,
                  lng: selectedOrder.currentLocation.longitude,
                },
                map: modalMap,
                title: `${selectedOrder.orderId} - ${selectedOrder.customerName} (Current Location)`,
                icon: {
                  url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                     <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                       <circle cx="16" cy="16" r="14" fill="#f59e0b" stroke="#d97706" stroke-width="3"/>
                       <text x="16" y="20" text-anchor="middle" fill="white" font-size="12" font-family="Arial, sans-serif">
                         ${
                           selectedOrder.orderType === "food"
                             ? "🍕"
                             : selectedOrder.orderType === "parcel"
                             ? "📦"
                             : "🛒"
                         }
                       </text>
                     </svg>
                   `)}`,
                  scaledSize: new window.google.maps.Size(32, 32),
                  anchor: new window.google.maps.Point(16, 16),
                },
              });
            } else {
              // Add a default marker at the center if no location data
              orderMarker = new window.google.maps.Marker({
                position: center,
                map: modalMap,
                title: `${selectedOrder.orderId} - ${selectedOrder.customerName} (Default Location)`,
                icon: {
                  url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                     <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                       <circle cx="16" cy="16" r="14" fill="#6b7280" stroke="#4b5563" stroke-width="3"/>
                       <text x="16" y="20" text-anchor="middle" fill="white" font-size="12" font-family="Arial, sans-serif">
                         ${
                           selectedOrder.orderType === "food"
                             ? "🍕"
                             : selectedOrder.orderType === "parcel"
                             ? "📦"
                             : "🛒"
                         }
                       </text>
                     </svg>
                   `)}`,
                  scaledSize: new window.google.maps.Size(32, 32),
                  anchor: new window.google.maps.Point(16, 16),
                },
              });
            }

            // Add info window for order details
            const infoWindow: google.maps.InfoWindow =
              new window.google.maps.InfoWindow({
                content: `
                 <div style="padding: 10px; min-width: 200px;">
                   <h3 style="margin: 0 0 8px 0; color: #1f2937;">${
                     selectedOrder.orderId
                   }</h3>
                   <p style="margin: 4px 0; color: #6b7280;"><strong>Customer:</strong> ${
                     selectedOrder.customerName
                   }</p>
                   <p style="margin: 4px 0; color: #6b7280;"><strong>Status:</strong> ${selectedOrder.status.replace(
                     "_",
                     " "
                   )}</p>
                   <p style="margin: 4px 0; color: #6b7280;"><strong>Type:</strong> ${
                     selectedOrder.orderType
                   }</p>
                   <p style="margin: 4px 0; color: #6b7280;"><strong>Priority:</strong> ${
                     selectedOrder.priority
                   }</p>
                   <hr style="margin: 8px 0; border: none; border-top: 1px solid #e5e7eb;">
                   <p style="margin: 4px 0; color: #6b7280;"><strong>Pickup:</strong> ${
                     selectedOrder.pickupAddress
                   }</p>
                   <p style="margin: 4px 0; color: #6b7280;"><strong>Delivery:</strong> ${
                     selectedOrder.deliveryAddress
                   }</p>
                   ${
                     selectedOrder.currentLocation
                       ? `
                     <hr style="margin: 8px 0; border: none; border-top: 1px solid #e5e7eb;">
                     <p style="margin: 4px 0; color: #6b7280;"><strong>Current Location:</strong></p>
                     <p style="margin: 2px 0; color: #6b7280; font-family: monospace;">
                       ${selectedOrder.currentLocation.latitude.toFixed(
                         6
                       )}, ${selectedOrder.currentLocation.longitude.toFixed(6)}
                     </p>
                     <p style="margin: 2px 0; color: #6b7280; font-size: 11px;">
                       Last updated: ${new Date(
                         selectedOrder.currentLocation.lastUpdated
                       ).toLocaleTimeString()}
                     </p>
                   `
                       : ""
                   }
                 </div>
               `,
              });

            // Add click listener to show info window
            if (orderMarker) {
              orderMarker.addListener("click", () => {
                infoWindow.open(modalMap, orderMarker!);
              });
            }

            // Add route visualization if we have current location
            if (selectedOrder.currentLocation) {
              // Create a simple route line from current location to delivery address
              // For now, we'll just show a dashed line to indicate the route
              // const routePath: google.maps.Polyline =
              //   new window.google.maps.Polyline({
              //     path: [
              //       {
              //         lat: selectedOrder.currentLocation.latitude,
              //         lng: selectedOrder.currentLocation.longitude,
              //       },
              //       center, // For now, just show a line to the center
              //     ],
              //     geodesic: true,
              //     strokeColor: "#f59e0b",
              //     strokeOpacity: 0.8,
              //     strokeWeight: 3,
              //     strokePattern: [10, 5], // Dashed line
              //     map: modalMap,
              //   });
            }
          } catch (error) {
            // Error creating modal map - silently fail
          }
        }
      };

      // Small delay to ensure DOM is ready
      setTimeout(initializeModalMap, 200);
    }
  }, [showOrderDetails, selectedOrder]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-gray-100 text-gray-800";
      case "assigned":
        return "bg-blue-100 text-blue-800";
      case "picked_up":
        return "bg-yellow-100 text-yellow-800";
      case "in_transit":
        return "bg-purple-100 text-purple-800";
      case "delivered":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "low":
        return "bg-green-100 text-green-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "high":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getOrderTypeIcon = (type: string) => {
    switch (type) {
      case "food":
        return "🍕";
      case "parcel":
        return "📦";
      case "grocery":
        return "🛒";
      default:
        return "📋";
    }
  };

  const filteredOrders = orders.filter((order) => {
    const matchesSearch =
      order.orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customerAddress.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || order.status === statusFilter;
    const matchesType =
      orderTypeFilter === "all" || order.orderType === orderTypeFilter;
    const matchesPriority =
      priorityFilter === "all" || order.priority === priorityFilter;

    return matchesSearch && matchesStatus && matchesType && matchesPriority;
  });

  const stats = {
    total: orders.length,
    inTransit: orders.filter((o: TrackingOrder) => o.status === "in_transit")
      .length,
    pending: orders.filter((o: TrackingOrder) => o.status === "pending").length,
    delivered: orders.filter((o: TrackingOrder) => o.status === "delivered")
      .length,
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen">
          <FoodLoader />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          {/* Title and Description */}
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-amber-900 leading-tight">
              Food Orders & Parcel Deliveries Tracking
            </h1>
            <p className="text-amber-700 mt-2 text-sm sm:text-base max-w-4xl">
              Track food orders from restaurants and parcel deliveries from
              logistics. Use tracking APIs for real-time coordinates.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-center gap-3 flex-shrink-0">
            {/* View Toggle Button */}
            <button
              onClick={() => setMapView(!mapView)}
              className={`w-full sm:w-auto flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-3 rounded-lg font-medium transition-colors text-sm sm:text-base ${
                mapView
                  ? "bg-amber-100 text-amber-700 hover:bg-amber-200 border border-amber-300"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
              }`}
            >
              <FiMap className="w-4 h-4" />
              <span className="hidden sm:inline">
                {mapView ? "Switch to Table" : "Switch to Map"}
              </span>
              <span className="sm:hidden">{mapView ? "Table" : "Map"}</span>
            </button>

            {/* Refresh Button */}
            <button
              onClick={handleRefreshTracking}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-3 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 transition-colors shadow-sm text-sm sm:text-base"
            >
              <FiRefreshCw className="w-4 h-4" />
              <span className="hidden sm:inline">Refresh Tracking</span>
              <span className="sm:hidden">Refresh</span>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          <div className="bg-white p-3 sm:p-6 rounded-xl shadow-sm border border-amber-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-amber-600">
                  Total Deliveries
                </p>
                <p className="text-lg sm:text-2xl font-bold text-amber-900">
                  {stats.total}
                </p>
              </div>
              <div className="p-2 sm:p-3 bg-amber-100 rounded-lg">
                <FiPackage className="w-4 h-4 sm:w-6 sm:h-6 text-amber-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-3 sm:p-6 rounded-xl shadow-sm border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-blue-600">
                  In Transit
                </p>
                <p className="text-lg sm:text-2xl font-bold text-blue-900">
                  {stats.inTransit}
                </p>
              </div>
              <div className="p-2 sm:p-3 bg-blue-100 rounded-lg">
                <FiTruck className="w-4 h-4 sm:w-6 sm:h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-3 sm:p-6 rounded-xl shadow-sm border border-yellow-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-yellow-600">
                  Pending
                </p>
                <p className="text-lg sm:text-2xl font-bold text-yellow-900">
                  {stats.pending}
                </p>
              </div>
              <div className="p-2 sm:p-3 bg-yellow-100 rounded-lg">
                <FiClock className="w-4 h-4 sm:w-6 sm:h-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-3 sm:p-6 rounded-xl shadow-sm border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-green-600">
                  Delivered
                </p>
                <p className="text-lg sm:text-2xl font-bold text-green-900">
                  {stats.delivered}
                </p>
              </div>
              <div className="p-2 sm:p-3 bg-green-100 rounded-lg">
                <FiMapPin className="w-4 h-4 sm:w-6 sm:h-6 text-green-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-amber-200">
          <div className="space-y-4">
            {/* Search Bar - Full Width on Mobile */}
            <div className="w-full">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search orders, customers, or addresses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm sm:text-base"
                />
              </div>
            </div>

            {/* Filter Dropdowns - Stack on Mobile, Row on Larger Screens */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <CustomSelect
                value={statusFilter}
                onChange={(value) => setStatusFilter(value as string)}
                options={[
                  { value: "all", label: "All Statuses" },
                  { value: "pending", label: "Pending" },
                  { value: "assigned", label: "Assigned" },
                  { value: "picked_up", label: "Picked Up" },
                  { value: "in_transit", label: "In Transit" },
                  { value: "delivered", label: "Delivered" },
                  { value: "cancelled", label: "Cancelled" },
                ]}
                placeholder="Filter by Status"
                className="w-full"
              />

              <CustomSelect
                value={orderTypeFilter}
                onChange={(value) => setOrderTypeFilter(value as string)}
                options={[
                  { value: "all", label: "All Types" },
                  { value: "food", label: "Food" },
                  { value: "parcel", label: "Parcel" },
                  { value: "grocery", label: "Grocery" },
                ]}
                placeholder="Filter by Type"
                className="w-full"
              />

              <CustomSelect
                value={priorityFilter}
                onChange={(value) => setPriorityFilter(value as string)}
                options={[
                  { value: "all", label: "All Priorities" },
                  { value: "low", label: "Low" },
                  { value: "medium", label: "Medium" },
                  { value: "high", label: "High" },
                ]}
                placeholder="Filter by Priority"
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* Orders Table or Map View */}
        {/* NOTE: Using raw orders instead of filteredOrders for map testing */}
        {mapView ? (
          <OrderMap
            orders={orders} // Temporarily use raw orders instead of filteredOrders for testing
            centerLat={9.082} // Abuja coordinates
            centerLng={8.6753}
            zoom={10}
          />
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-amber-200 overflow-hidden">
            <div className="px-4 sm:px-6 py-4 border-b border-amber-200 bg-amber-50">
              <h2 className="text-lg sm:text-xl font-semibold text-amber-900">
                Food Orders & Parcel Deliveries
              </h2>
              <p className="text-xs sm:text-sm text-amber-700 mt-1">
                Track food orders, parcel deliveries, and grocery orders in
                real-time
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead className="bg-amber-50">
                  <tr>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-amber-700 uppercase tracking-wider">
                      Order Details
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-amber-700 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-amber-700 uppercase tracking-wider">
                      Agent
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-amber-700 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-amber-700 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-amber-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-amber-100">
                  {filteredOrders.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-6 py-12 text-center text-gray-500"
                      >
                        <div className="flex flex-col items-center gap-2">
                          <FiPackage className="w-12 h-12 text-gray-300" />
                          <p className="text-lg font-medium">No orders found</p>
                          <p className="text-sm">
                            Try adjusting your filters or search terms
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredOrders.map((order: TrackingOrder) => (
                      <tr
                        key={order.id}
                        className="hover:bg-amber-50 transition-colors"
                      >
                        <td className="px-3 sm:px-6 py-3 sm:py-4">
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className="text-xl sm:text-2xl">
                              {getOrderTypeIcon(order.orderType)}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-gray-900 text-sm sm:text-base truncate">
                                  {order.orderId}
                                </p>
                                {order.parcelId && (
                                  <span className="text-xs bg-gray-100 text-gray-600 px-1 sm:px-2 py-1 rounded flex-shrink-0">
                                    {order.parcelId}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <span
                                  className={`inline-flex items-center px-1 sm:px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(
                                    order.priority
                                  )}`}
                                >
                                  {order.priority}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {new Date(
                                    order.createdAt
                                  ).toLocaleDateString()}
                                </span>
                              </div>
                              <div className="text-xs sm:text-sm text-gray-600 mt-1">
                                <div className="flex items-center gap-1">
                                  <FiMapPin className="w-3 h-3 flex-shrink-0" />
                                  <span className="truncate max-w-[120px] sm:max-w-xs">
                                    {order.pickupAddress} →{" "}
                                    {order.deliveryAddress}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>

                        <td className="px-3 sm:px-6 py-3 sm:py-4">
                          <div>
                            <p className="font-medium text-gray-900 text-sm sm:text-base">
                              {order.customerName}
                            </p>
                            <p className="text-xs sm:text-sm text-gray-600">
                              {order.customerPhone}
                            </p>
                            <p className="text-xs text-gray-500 truncate max-w-[120px] sm:max-w-xs mt-1">
                              {order.customerAddress}
                            </p>
                          </div>
                        </td>

                        <td className="px-3 sm:px-6 py-3 sm:py-4">
                          {order.assignedAgent ? (
                            <div>
                              <p className="font-medium text-gray-900 text-sm sm:text-base">
                                {order.assignedAgent.name}
                              </p>
                              <p className="text-xs sm:text-sm text-gray-600">
                                {order.assignedAgent.phone}
                              </p>
                              <p className="text-xs text-gray-500">
                                {order.assignedAgent.vehicleType}
                              </p>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs sm:text-sm">
                              Unassigned
                            </span>
                          )}
                        </td>

                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                              order.status
                            )}`}
                          >
                            {order.status
                              .replace("_", " ")
                              .replace(/\b\w/g, (l: string) => l.toUpperCase())}
                          </span>
                          {order.estimatedDelivery && (
                            <p className="text-xs text-gray-500 mt-1">
                              ETA:{" "}
                              {new Date(
                                order.estimatedDelivery
                              ).toLocaleTimeString()}
                            </p>
                          )}
                        </td>

                        <td className="px-6 py-4">
                          {order.currentLocation ? (
                            <div className="text-sm">
                              <div className="flex items-center gap-1 text-gray-900">
                                <FiMapPin className="w-3 h-3" />
                                <span>
                                  {order.currentLocation.latitude.toFixed(4)},{" "}
                                  {order.currentLocation.longitude.toFixed(4)}
                                </span>
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                <div className="flex items-center gap-1">
                                  <FiClock className="w-3 h-3" />
                                  <span>
                                    {new Date(
                                      order.currentLocation.lastUpdated
                                    ).toLocaleTimeString()}
                                  </span>
                                </div>
                                {order.currentLocation.speed && (
                                  <div className="flex items-center gap-1 mt-1">
                                    <FiNavigation className="w-3 h-3" />
                                    <span className="text-xs text-gray-500">
                                      {order.currentLocation.speed} km/h
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">
                              No location data
                            </span>
                          )}
                        </td>

                        <td className="px-6 py-4">
                          <button
                            onClick={() => handleViewOrder(order)}
                            className="flex items-center gap-2 px-3 py-2 text-sm bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors"
                          >
                            <FiEye className="w-4 h-4" />
                            Track
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      {showOrderDetails && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">
                  Order Details
                </h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      // Refresh the modal map
                      if (selectedOrder && window.google) {
                        const mapElement = document.getElementById(
                          `modal-map-${selectedOrder.id}`
                        );
                        if (mapElement) {
                          // Force map re-initialization
                          mapElement.innerHTML = "";
                          setTimeout(() => {
                            const modalMap = new window.google.maps.Map(
                              mapElement,
                              {
                                center: selectedOrder.currentLocation
                                  ? {
                                      lat: selectedOrder.currentLocation
                                        .latitude,
                                      lng: selectedOrder.currentLocation
                                        .longitude,
                                    }
                                  : { lat: 9.082, lng: 8.6753 },
                                zoom: 12,
                                mapTypeId: window.google.maps.MapTypeId.ROADMAP,
                                styles: [
                                  {
                                    featureType: "administrative.locality",
                                    elementType: "labels.text",
                                    stylers: [{ visibility: "on" }],
                                  },
                                  {
                                    featureType: "administrative.neighborhood",
                                    elementType: "labels.text",
                                    stylers: [{ visibility: "on" }],
                                  },
                                  {
                                    featureType: "poi",
                                    elementType: "labels.text",
                                    stylers: [{ visibility: "on" }],
                                  },
                                  {
                                    featureType: "road",
                                    elementType: "labels.text",
                                    stylers: [{ visibility: "on" }],
                                  },
                                ],
                                disableDefaultUI: false,
                                zoomControl: true,
                                mapTypeControl: true,
                                scaleControl: true,
                                streetViewControl: true,
                                fullscreenControl: true,
                                gestureHandling: "cooperative",
                                backgroundColor: "#f8f9fa",
                                clickableIcons: true,
                                draggable: true,
                                scrollwheel: true,
                              }
                            );

                            // Add marker for the selected order
                            if (selectedOrder.currentLocation) {
                              new window.google.maps.Marker({
                                position: {
                                  lat: selectedOrder.currentLocation.latitude,
                                  lng: selectedOrder.currentLocation.longitude,
                                },
                                map: modalMap,
                                title: `${selectedOrder.orderId} - ${selectedOrder.customerName}`,
                                icon: {
                                  url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                                    <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                                      <circle cx="16" cy="16" r="14" fill="#f59e0b" stroke="#d97706" stroke-width="3"/>
                                      <text x="16" y="20" text-anchor="middle" fill="white" font-size="12" font-family="Arial, sans-serif">
                                        ${
                                          selectedOrder.orderType === "food"
                                            ? "🍕"
                                            : selectedOrder.orderType ===
                                              "parcel"
                                            ? "📦"
                                            : "🛒"
                                        }
                                      </text>
                                    </svg>
                                  `)}`,
                                  scaledSize: new window.google.maps.Size(
                                    32,
                                    32
                                  ),
                                  anchor: new window.google.maps.Point(16, 16),
                                },
                              });
                            }
                          }, 100);
                        }
                      }
                    }}
                    className="p-2 text-amber-600 hover:text-amber-700 transition-colors"
                    title="Refresh Map"
                  >
                    <FiRefreshCw className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setShowOrderDetails(false)}
                    className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <FiX className="w-6 h-6" />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Order Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Order Information
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Order ID:</span>
                      <span className="font-medium">
                        {selectedOrder.orderId}
                      </span>
                    </div>
                    {selectedOrder.parcelId && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Parcel ID:</span>
                        <span className="font-medium">
                          {selectedOrder.parcelId}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                          selectedOrder.status
                        )}`}
                      >
                        {selectedOrder.status
                          .replace("_", " ")
                          .replace(/\b\w/g, (l: string) => l.toUpperCase())}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Priority:</span>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(
                          selectedOrder.priority
                        )}`}
                      >
                        {selectedOrder.priority}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Type:</span>
                      <span className="font-medium">
                        {selectedOrder.orderType}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Customer Information
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Name:</span>
                      <span className="font-medium">
                        {selectedOrder.customerName}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Phone:</span>
                      <span className="font-medium">
                        {selectedOrder.customerPhone}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Address:</span>
                      <span className="font-medium text-right max-w-xs">
                        {selectedOrder.customerAddress}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Addresses */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Delivery Route
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-2">
                      Pickup Location
                    </h4>
                    <p className="text-blue-800">
                      {selectedOrder.pickupAddress}
                    </p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-medium text-green-900 mb-2">
                      Delivery Location
                    </h4>
                    <p className="text-green-800">
                      {selectedOrder.deliveryAddress}
                    </p>
                  </div>
                </div>
              </div>

              {/* Agent Info */}
              {selectedOrder.assignedAgent && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Assigned Agent
                  </h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <span className="text-gray-600 text-sm">Name:</span>
                        <p className="font-medium">
                          {selectedOrder.assignedAgent.name}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-600 text-sm">Phone:</span>
                        <p className="font-medium">
                          {selectedOrder.assignedAgent.phone}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-600 text-sm">Vehicle:</span>
                        <p className="font-medium">
                          {selectedOrder.assignedAgent.vehicleType}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Current Location */}
              {selectedOrder.currentLocation && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Current Location
                  </h3>
                  <div className="bg-amber-50 p-4 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="text-gray-600 text-sm">
                          Coordinates:
                        </span>
                        <p className="font-medium">
                          {selectedOrder.currentLocation.latitude.toFixed(6)},{" "}
                          {selectedOrder.currentLocation.longitude.toFixed(6)}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-600 text-sm">
                          Last Updated:
                        </span>
                        <p className="font-medium">
                          {new Date(
                            selectedOrder.currentLocation.lastUpdated
                          ).toLocaleString()}
                        </p>
                      </div>
                      {selectedOrder.currentLocation.speed && (
                        <div>
                          <span className="text-gray-600 text-sm">Speed:</span>
                          <p className="font-medium">
                            {selectedOrder.currentLocation.speed} km/h
                          </p>
                        </div>
                      )}
                      {selectedOrder.currentLocation.heading && (
                        <div>
                          <span className="text-gray-600 text-sm">
                            Direction:
                          </span>
                          <p className="font-medium">
                            {selectedOrder.currentLocation.heading}°
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Live Tracking Map */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  🗺️ Live Tracking Map
                </h3>
                <div className="bg-gray-100 rounded-lg overflow-hidden border-2 border-dashed border-gray-300">
                  <div className="h-80 relative">
                    {window.google ? (
                      <div
                        id={`modal-map-${selectedOrder.id}`}
                        className="w-full h-full"
                        style={{ minHeight: "320px" }}
                      />
                    ) : (
                      <div className="absolute inset-0 bg-gradient-to-br from-amber-50 to-blue-50 flex items-center justify-center">
                        <div className="text-center text-gray-500">
                          <FiMap className="w-16 h-16 mx-auto mb-2 text-gray-300" />
                          <p className="text-sm">Google Maps Not Available</p>
                          <p className="text-xs mb-4">
                            Please check your internet connection
                          </p>

                          {/* Fallback: Show order location info */}
                          {selectedOrder.currentLocation ? (
                            <div className="bg-white p-4 rounded-lg border max-w-sm mx-auto">
                              <h4 className="font-medium text-gray-800 mb-2">
                                📍 Order Location
                              </h4>
                              <div className="text-sm space-y-1">
                                <p>
                                  <strong>Latitude:</strong>{" "}
                                  {selectedOrder.currentLocation.latitude.toFixed(
                                    6
                                  )}
                                </p>
                                <p>
                                  <strong>Longitude:</strong>{" "}
                                  {selectedOrder.currentLocation.longitude.toFixed(
                                    6
                                  )}
                                </p>
                                <p>
                                  <strong>Last Updated:</strong>{" "}
                                  {new Date(
                                    selectedOrder.currentLocation.lastUpdated
                                  ).toLocaleString()}
                                </p>
                                {selectedOrder.currentLocation.speed && (
                                  <p>
                                    <strong>Speed:</strong>{" "}
                                    {selectedOrder.currentLocation.speed} km/h
                                  </p>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="bg-white p-4 rounded-lg border max-w-sm mx-auto">
                              <h4 className="font-medium text-gray-800 mb-2">
                                📍 Order Location
                              </h4>
                              <p className="text-sm text-gray-600">
                                No location data available for this order.
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                The order may be pending or not yet assigned to
                                a rider.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Map Status and Controls */}
                <div className="mt-3 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Map Status:</span>
                      <span
                        className={`px-2 py-1 rounded text-xs ${
                          window.google
                            ? "bg-green-100 text-green-700"
                            : "bg-yellow-100 text-yellow-700"
                        }`}
                      >
                        {window.google ? "Ready" : "Loading..."}
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        if (selectedOrder && window.google) {
                          const mapElement = document.getElementById(
                            `modal-map-${selectedOrder.id}`
                          );
                          if (mapElement) {
                            // Force map re-initialization
                            mapElement.innerHTML = "";
                            setTimeout(() => {
                              const modalMap = new window.google.maps.Map(
                                mapElement,
                                {
                                  center: selectedOrder.currentLocation
                                    ? {
                                        lat: selectedOrder.currentLocation
                                          .latitude,
                                        lng: selectedOrder.currentLocation
                                          .longitude,
                                      }
                                    : { lat: 9.082, lng: 8.6753 },
                                  zoom: 12,
                                  mapTypeId:
                                    window.google.maps.MapTypeId.ROADMAP,
                                  styles: [
                                    {
                                      featureType: "administrative.locality",
                                      elementType: "labels.text",
                                      stylers: [{ visibility: "on" }],
                                    },
                                    {
                                      featureType:
                                        "administrative.neighborhood",
                                      elementType: "labels.text",
                                      stylers: [{ visibility: "on" }],
                                    },
                                    {
                                      featureType: "poi",
                                      elementType: "labels.text",
                                      stylers: [{ visibility: "on" }],
                                    },
                                    {
                                      featureType: "road",
                                      elementType: "labels.text",
                                      stylers: [{ visibility: "on" }],
                                    },
                                  ],
                                  disableDefaultUI: false,
                                  zoomControl: true,
                                  mapTypeControl: true,
                                  scaleControl: true,
                                  streetViewControl: true,
                                  fullscreenControl: true,
                                  gestureHandling: "cooperative",
                                  backgroundColor: "#f8f9fa",
                                  clickableIcons: true,
                                  draggable: true,
                                  scrollwheel: true,
                                }
                              );

                              // Add marker for the selected order
                              if (selectedOrder.currentLocation) {
                                new window.google.maps.Marker({
                                  position: {
                                    lat: selectedOrder.currentLocation.latitude,
                                    lng: selectedOrder.currentLocation
                                      .longitude,
                                  },
                                  map: modalMap,
                                  title: `${selectedOrder.orderId} - ${selectedOrder.customerName}`,
                                  icon: {
                                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                                      <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="16" cy="16" r="14" fill="#f59e0b" stroke="#d97706" stroke-width="3"/>
                                        <text x="16" y="20" text-anchor="middle" fill="white" font-size="12" font-family="Arial, sans-serif">
                                          ${
                                            selectedOrder.orderType === "food"
                                              ? "🍕"
                                              : selectedOrder.orderType ===
                                                "parcel"
                                              ? "📦"
                                              : "🛒"
                                          }
                                        </text>
                                      </svg>
                                    `)}`,
                                    scaledSize: new window.google.maps.Size(
                                      32,
                                      32
                                    ),
                                    anchor: new window.google.maps.Point(
                                      16,
                                      16
                                    ),
                                  },
                                });
                              } else {
                                // Add a default marker at the center if no location data
                                new window.google.maps.Marker({
                                  position: { lat: 9.082, lng: 8.6753 },
                                  map: modalMap,
                                  title: `${selectedOrder.orderId} - ${selectedOrder.customerName} (Default Location)`,
                                  icon: {
                                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                                      <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="16" cy="16" r="14" fill="#6b7280" stroke="#4b5563" stroke-width="3"/>
                                        <text x="16" y="20" text-anchor="middle" fill="white" font-size="12" font-family="Arial, sans-serif">
                                          ${
                                            selectedOrder.orderType === "food"
                                              ? "🍕"
                                              : selectedOrder.orderType ===
                                                "parcel"
                                              ? "📦"
                                              : "🛒"
                                          }
                                        </text>
                                      </svg>
                                    `)}`,
                                    scaledSize: new window.google.maps.Size(
                                      32,
                                      32
                                    ),
                                    anchor: new window.google.maps.Point(
                                      16,
                                      16
                                    ),
                                  },
                                });
                              }
                            }, 100);
                          }
                        }
                      }}
                      className="px-3 py-1 bg-amber-600 text-white rounded text-xs hover:bg-amber-700 transition-colors"
                    >
                      🔄 Reinitialize Map
                    </button>
                  </div>

                  <div className="text-xs text-gray-500 text-center">
                    💡 The map shows the order's current location. Use the
                    refresh button above to update the map.
                  </div>
                </div>
              </div>

              {/* Timestamps */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Timeline
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Created:</span>
                    <span className="font-medium">
                      {new Date(selectedOrder.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Last Updated:</span>
                    <span className="font-medium">
                      {new Date(selectedOrder.updatedAt).toLocaleString()}
                    </span>
                  </div>
                  {selectedOrder.estimatedDelivery && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Estimated Delivery:</span>
                      <span className="font-medium">
                        {new Date(
                          selectedOrder.estimatedDelivery
                        ).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowOrderDetails(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default ActiveRiders;
